import Logger from './logger';
import { isObject } from './utils';

/**
 * Settings key holding the region-keyed override map.
 */
export const GEO_OVERRIDES_KEY = 'geoOverrides';

/**
 * Keys that could be used to reach the prototype chain. Never merged, at any depth.
 */
const UNSAFE_KEYS = ['__proto__', 'constructor', 'prototype'];

/**
 * Apply the `geoOverrides` entry for a region's country onto settings.
 *
 * Only country-level overrides are supported: a region of `US-CA` applies the
 * `US` entry, so the map is keyed by country alone. Keys are matched exactly
 * and must be uppercase ISO 3166-1 alpha-2 codes, as Fastly sends them.
 *
 * Pure: `settings` is never mutated, and the same object is returned when there
 * is nothing to apply. No matching entry means no override — there is no fail mode.
 *
 * @param {object} settings Computed settings
 * @param {string?} region Region from the `geo-region` header, e.g. `US-CA`
 * @returns {object} Settings with the country's overrides applied
 */
export function applyGeoOverrides(settings, region) {
  if (!isPlainObject(settings)) return settings;

  const geoOverrides = settings[GEO_OVERRIDES_KEY];
  if (!isPlainObject(geoOverrides)) return settings;

  const country = regionCountry(region);
  if (!country || !Object.prototype.hasOwnProperty.call(geoOverrides, country)) return settings;

  const entry = geoOverrides[country];
  if (!isPlainObject(entry)) return settings;

  const overrides = overridableValues(entry);
  const changedKeys = Object.keys(overrides);
  if (!changedKeys.length) return settings;

  Logger.log(`Applied geo overrides for ${country}`, {
    region,
    country,
    changedKeys,
  });

  return mergeOverrides(settings, overrides);
}

/**
 * The country code to look up from a region header.
 *
 * The header is always empty, `XX` (country) or `XX-YY` (country-subdivision),
 * and only the country is used, so both `US` and `US-CA` give `US`. Mind that a
 * bare `CA` is the country Canada, not California.
 *
 * @see https://www.fastly.com/documentation/reference/vcl/variables/geolocation/client-geo-region-ascii/
 * @param {string?} region Region from the `geo-region` header
 * @returns {string} The country code, or an empty string
 */
export function regionCountry(region) {
  if (typeof region !== 'string') return '';

  return region.split('-')[0];
}

/**
 * The values of an entry that are allowed to override settings: everything
 * except a `geoOverrides` map nested inside it (which is ignored, not resolved)
 * and the unsafe prototype keys.
 *
 * @param {object} entry An override entry
 * @returns {object} The overridable subset of the entry
 */
function overridableValues(entry) {
  return Object.keys(entry).reduce((values, key) => {
    if (key === GEO_OVERRIDES_KEY || UNSAFE_KEYS.includes(key)) return values;

    values[key] = entry[key];

    return values;
  }, {});
}

/**
 * Deep-merge `overrides` onto `base` and return a new object. Plain objects
 * merge recursively; arrays, scalars and `null` replace outright. Note that
 * arrays are NOT concatenated here, unlike the settings cascade in
 * cascading_config.js — an override states the whole list it wants.
 *
 * @param {object} base Object to merge onto
 * @param {object} overrides Values to merge in
 * @returns {object} New object; neither argument is mutated
 */
function mergeOverrides(base, overrides) {
  const merged = { ...base };

  Object.keys(overrides).forEach(key => {
    if (UNSAFE_KEYS.includes(key)) return;

    const overrideValue = overrides[key];

    if (!isPlainObject(overrideValue)) {
      merged[key] = overrideValue;
      return;
    }

    // Recurse even when there is nothing to merge onto, so the override subtree
    // is copied rather than aliased into live settings.
    merged[key] = mergeOverrides(isPlainObject(merged[key]) ? merged[key] : {}, overrideValue);
  });

  return merged;
}

/**
 * Whether a value is an object that should be merged into, rather than replaced.
 * Arrays and null are not.
 *
 * @param {any} value
 * @returns {boolean}
 */
function isPlainObject(value) {
  return Boolean(isObject(value)) && !Array.isArray(value);
}
